import { useMemo, useState } from "react";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "../../lib/api";
import {
  useResource, PageTitle, Panel, Loading, ErrorState, EmptyState, Pill, DemoBadge,
  STATUS_TONE, fmtDateTime, fmtDuration, Metric,
} from "../../components/app/Kit";

const FILTERS = ["all", "inbound", "outbound", "missed"];
const SENTIMENT_TONE = {
  positive: "green", negative: "amber", mixed: "cyan", neutral: "dim", unknown: "dim",
};

export default function Calls() {
  const { data, state, error, reload } = useResource("/calls");
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [callData, setCallData] = useState({});

  const rows = useMemo(() => {
    const list = data || [];
    if (filter === "all") return list;
    if (filter === "missed") return list.filter((c) => c.status === "missed");
    return list.filter((c) => c.direction === filter);
  }, [data, filter]);

  const stats = useMemo(() => {
    const list = data || [];
    return {
      inbound: list.filter((c) => c.direction === "inbound").length,
      outbound: list.filter((c) => c.direction === "outbound").length,
      missed: list.filter((c) => c.status === "missed").length,
      ai: list.filter((c) => c.handled_by === "ai").length,
    };
  }, [data]);

  const baseCall = selected ? (rows.find((c) => c.id === selected) || (data || []).find((c) => c.id === selected)) : null;
  const call = baseCall ? { ...baseCall, ...(callData[baseCall.id] || {}) } : null;
  const insights = call?.insights;
  const hasTranscript = call?.transcript && call.transcript.length > 0;

  const analyze = async () => {
    if (!call) return;
    setAnalyzing(true);
    try {
      const { data: res } = await api.post(`/calls/${call.id}/analyze`);
      setCallData((s) => ({ ...s, [call.id]: { ...s[call.id], insights: res, summary: res.summary,
        sentiment: res.sentiment, intent: res.intent, outcome: res.outcome,
        next_action: res.follow_up_recommendation } }));
      toast.success("AI insights generated.");
    } catch (e) { toast.error(apiError(e)); }
    finally { setAnalyzing(false); }
  };

  return (
    <div data-testid="calls-page">
      <PageTitle testId="calls-title" title="Call centre"
                 sub="Inbound, outbound and missed calls with AI-generated summaries, sentiment and next steps."
                 action={<DemoBadge />} />

      <div data-testid="voice-integration-notice"
           className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-amber-300/25 bg-amber-300/[0.05] p-4">
        <p className="text-xs text-amber-200">
          <span className="font-medium">Vapi not configured.</span> Call records below are demonstration data. Connect a voice provider to handle real calls — every completed call is auto-summarised by Claude Sonnet 4.6.
        </p>
        <a href="/app/integrations" data-testid="calls-connect-voice"
           className="rounded-md border border-amber-300/30 px-3 py-1.5 text-[11px] text-amber-200 hover:bg-amber-300/10">
          Connect Voice Provider
        </a>
      </div>

      <div className="grid gap-3 sm:grid-cols-4">
        <Metric testId="calls-metric-inbound" label="Inbound" value={stats.inbound} />
        <Metric testId="calls-metric-outbound" label="Outbound" value={stats.outbound} tone="cyan" />
        <Metric testId="calls-metric-missed" label="Missed" value={stats.missed} tone="cyan" />
        <Metric testId="calls-metric-ai" label="AI handled" value={stats.ai} />
      </div>

      <div className="mt-4 flex flex-wrap gap-1.5">
        {FILTERS.map((f) => (
          <button key={f} type="button" onClick={() => setFilter(f)} data-testid={`calls-filter-${f}`}
                  className={`rounded-md border px-3 py-1.5 text-[11px] uppercase tracking-[0.12em] ${
                    filter === f ? "border-elv-green/35 bg-elv-green/[0.08] text-elv-green" : "border-elv-line bg-white/[0.02] text-elv-dim"
                  }`}>
            {f}
          </button>
        ))}
      </div>

      <div className="mt-4 grid gap-4 lg:grid-cols-[1.1fr_1fr]">
        <div>
          {state === "loading" ? <Loading rows={8} testId="calls-loading" /> : null}
          {state === "error" ? <ErrorState message={error} onRetry={reload} testId="calls-error" /> : null}
          {state === "ready" && rows.length === 0 ? (
            <EmptyState icon="PhoneCall" title="No calls yet"
                        sub="Your AI receptionist activity will appear here once a voice provider is connected."
                        testId="calls-empty" />
          ) : null}
          {state === "ready" && rows.length > 0 ? (
            <div className="grid gap-2">
              {rows.map((c) => (
                <button key={c.id} type="button" onClick={() => setSelected(c.id)} data-testid={`call-row-${c.id}`}
                        className={`glass glass-hover rounded-lg p-4 text-left ${selected === c.id ? "border-elv-green/35" : ""}`}>
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className="text-xs font-medium text-elv-text">{c.customer_name}</span>
                    <div className="flex items-center gap-2">
                      <Pill tone={c.handled_by === "ai" ? "green" : "dim"}>{c.handled_by}</Pill>
                      <Pill tone={STATUS_TONE[c.status] || "dim"}>{c.status}</Pill>
                      {c.sentiment ? <Pill tone={SENTIMENT_TONE[c.sentiment] || "dim"}>{c.sentiment}</Pill> : null}
                    </div>
                  </div>
                  <p className="mt-2 text-[11px] text-elv-dim">
                    {c.direction} · {c.phone} · {fmtDuration(c.duration_seconds)} · {fmtDateTime(c.created_at)}
                  </p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {c.lead_created ? <Pill tone="cyan">lead created</Pill> : null}
                    {c.appointment_booked ? <Pill tone="green">appointment booked</Pill> : null}
                    {!c.recording_available ? <Pill tone="dim">no recording</Pill> : null}
                    {c.insights ? <Pill tone="green">AI analysed</Pill> : null}
                  </div>
                </button>
              ))}
            </div>
          ) : null}
        </div>

        <div className="lg:sticky lg:top-20 lg:self-start">
          {call ? (
            <Panel testId="call-detail" title={`Call detail — ${call.customer_name}`}
                   action={hasTranscript ? (
                     <button type="button" data-testid="call-analyze-btn" onClick={analyze} disabled={analyzing}
                             className="inline-flex items-center gap-1.5 rounded-md bg-elv-green px-3 py-1.5 text-[11px] font-semibold text-[#04120A] disabled:opacity-60">
                       {analyzing ? <Icons.Loader2 className="h-3 w-3 animate-spin" /> : <Icons.Sparkles className="h-3 w-3" />}
                       {analyzing ? "Analysing…" : (insights ? "Re-analyse" : "Analyse with AI")}
                     </button>
                   ) : null}>
              <div className="grid gap-3">
                {[
                  ["Customer", call.customer_name],
                  ["Intent", call.intent],
                  ["Outcome", call.outcome],
                  ["Next action", call.next_action],
                  ["Sentiment", call.sentiment || "not analysed"],
                  ["Recording", call.recording_available ? "Available" : "Not available"],
                ].map(([k, v]) => (
                  <div key={k} className="flex items-center justify-between gap-3 text-xs">
                    <span className="text-elv-dim">{k}</span>
                    <span className="text-elv-text">{v}</span>
                  </div>
                ))}
                <div>
                  <p className="text-[10px] uppercase tracking-[0.16em] text-elv-dim">AI Summary</p>
                  <p data-testid="call-summary" className="mt-2 rounded-md border border-white/[0.06] bg-white/[0.02] p-3 text-[11px] text-elv-dim">
                    {call.summary || "No summary yet."}
                  </p>
                </div>

                {insights?.key_points?.length ? (
                  <div data-testid="call-key-points">
                    <p className="text-[10px] uppercase tracking-[0.16em] text-elv-dim">Key points</p>
                    <ul className="mt-2 grid gap-1.5">
                      {insights.key_points.map((k, i) => (
                        <li key={i} className="flex items-start gap-2 rounded-md border border-white/[0.06] bg-white/[0.02] px-3 py-2 text-[11px] text-elv-dim">
                          <Icons.Circle className="mt-1 h-1.5 w-1.5 shrink-0 fill-elv-green text-elv-green" />
                          <span>{k}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}

                {insights?.next_steps?.length ? (
                  <div data-testid="call-next-steps">
                    <p className="text-[10px] uppercase tracking-[0.16em] text-elv-dim">Suggested next steps</p>
                    <ul className="mt-2 grid gap-1.5">
                      {insights.next_steps.map((k, i) => (
                        <li key={i} className="flex items-start gap-2 rounded-md border border-elv-cyan/20 bg-elv-cyan/[0.05] px-3 py-2 text-[11px] text-elv-text">
                          <Icons.ArrowRight className="mt-0.5 h-3 w-3 shrink-0 text-elv-cyan" />
                          <span>{k}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}

                {insights?.follow_up_recommendation ? (
                  <p data-testid="call-followup" className="rounded-md border border-elv-green/25 bg-elv-green/[0.06] p-3 text-[11px] text-elv-text">
                    <span className="text-[10px] uppercase tracking-[0.16em] text-elv-green">Follow-up: </span>
                    {insights.follow_up_recommendation}
                  </p>
                ) : null}

                <div>
                  <p className="text-[10px] uppercase tracking-[0.16em] text-elv-dim">Transcript</p>
                  {!hasTranscript ? (
                    <p className="mt-2 rounded-md border border-white/[0.06] bg-white/[0.02] p-3 text-[11px] text-elv-dim">
                      No transcript available for this call.
                    </p>
                  ) : (
                    <div className="mt-2 grid gap-2">
                      {(call.transcript || []).map((t, i) => (
                        <div key={i} data-testid={`call-transcript-${i}`}
                             className={`rounded-md border px-3 py-2 text-[11px] ${
                               t.speaker === "ai"
                                 ? "border-elv-green/20 bg-elv-green/[0.06] text-elv-text"
                                 : "border-white/[0.06] bg-white/[0.02] text-elv-dim"
                             }`}>
                          <span className="mb-1 block text-[9px] uppercase tracking-[0.18em] text-elv-dim">{t.speaker}</span>
                          {t.text}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </Panel>
          ) : (
            <EmptyState icon="Headphones" title="Select a call"
                        sub="Choose a call to see its transcript, AI-generated summary, sentiment and suggested next steps."
                        testId="call-detail-empty" />
          )}
        </div>
      </div>
    </div>
  );
}
